import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, EmptyState, Page } from '@appwin/ui';
import { api, ApiError, type DeveloperApp, type DeveloperStats, type Session } from './api.js';
import { NewAppDialog } from './NewAppDialog.js';
import { AppDetail } from './AppDetail.js';

/**
 * Developer cabinet.
 *
 * The portal is a separate origin from the API, so the first `session()` call
 * does double duty: it checks whether the cookie is already valid and, on 401,
 * tells us to show the sign-in screen.
 */
export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .session()
      .then(setSession)
      .catch((cause: unknown) => {
        // 401 simply means "not signed in yet".
        if (cause instanceof ApiError && cause.status === 401) return;
        setError(cause instanceof Error ? cause.message : 'Не удалось проверить сессию');
      })
      .finally(() => setChecking(false));
  }, []);

  if (checking) {
    return (
      <Page>
        <p className="py-16 text-center text-sm text-muted">Загрузка…</p>
      </Page>
    );
  }

  if (error) {
    return (
      <Page>
        <div className="py-16 text-center">
          <p className="font-medium">Не удалось подключиться к серверу</p>
          <p className="mt-1 text-sm text-muted">{error}</p>
          <Button className="mt-4" onClick={() => window.location.reload()}>
            Повторить
          </Button>
        </div>
      </Page>
    );
  }

  return session ? (
    <Cabinet session={session} onSignOut={() => setSession(null)} />
  ) : (
    <SignIn />
  );
}

function SignIn() {
  return (
    <Page>
      <main className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center">
        <h1 className="text-2xl font-semibold">Кабинет разработчика</h1>
        <p className="mt-2 text-sm text-muted">
          Публикуйте приложения в каталоге Appwin и получайте статистику установок.
        </p>
        <div className="mt-8 space-y-3">
          <Button className="w-full" onClick={() => api.signInWithGithub()}>
            Продолжить с GitHub
          </Button>
          <p className="text-xs text-muted">
            Используем только ваше имя и подтверждённый email. Доступ к репозиториям не
            запрашивается.
          </p>
        </div>
      </main>
    </Page>
  );
}

function Cabinet({ session, onSignOut }: { session: Session; onSignOut: () => void }) {
  const [apps, setApps] = useState<DeveloperApp[] | null>(null);
  const [stats, setStats] = useState<DeveloperStats | null>(null);
  const [creating, setCreating] = useState(false);
  const [openApp, setOpenApp] = useState<string | null>(null);

  const reload = useCallback(() => {
    api.apps().then((page) => setApps(page.items));
    api.stats().then(setStats);
  }, []);

  // Hooks must run unconditionally, so the early return lives after them.
  useEffect(reload, [reload]);

  if (openApp) {
    return <AppDetail appId={openApp} onBack={() => setOpenApp(null)} />;
  }

  return (
    <Page>
      <header className="flex items-center justify-between border-b border-border py-4">
        <div>
          <p className="text-sm text-muted">Кабинет разработчика</p>
          <p className="font-medium">{session.displayName}</p>
        </div>
        <div className="flex items-center gap-2">
          {session.role === 'admin' ? <Badge tone="accent">администратор</Badge> : null}
          <Button
            variant="ghost"
            size="sm"
            onClick={async () => {
              await api.logout();
              onSignOut();
            }}
          >
            Выйти
          </Button>
        </div>
      </header>

      <main className="py-6">
        {stats ? (
          <dl className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Приложений" value={String(stats.apps)} />
            <Stat label="Опубликовано" value={String(stats.releasesPublished)} />
            <Stat label="Установок за 7 дней" value={String(stats.installs7d)} />
            <Stat
              label="Успешных установок"
              value={
                // Null means nobody reported anything: showing "0%" would read
                // as "everything fails".
                stats.successRate7d === null ? 'нет данных' : `${Math.round(stats.successRate7d * 100)}%`
              }
            />
          </dl>
        ) : null}

        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Мои приложения</h2>
          <Button size="sm" onClick={() => setCreating(true)}>
            Новое приложение
          </Button>
        </div>

        {apps === null ? (
          <p className="py-8 text-center text-sm text-muted">Загрузка…</p>
        ) : apps.length === 0 ? (
          <EmptyState
            title="Пока нет приложений"
            hint="Создайте первое приложение и загрузите установщик — после проверки оно появится в каталоге."
          />
        ) : (
          <ul className="list-none space-y-2 p-0">
            {apps.map((app) => (
              <li
                key={app.id}
                onClick={() => setOpenApp(app.id)}
                className="flex cursor-pointer items-center justify-between gap-4 rounded-xl border border-border bg-surface p-4 hover:border-muted"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium">{app.name}</p>
                  <p className="truncate text-xs text-muted">{app.id}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Badge tone={statusTone(app.status)}>{statusLabel(app.status)}</Badge>
                  <span className="text-xs text-muted">{app.releaseCount} версий</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </main>

      {creating ? (
        <NewAppDialog
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            reload();
          }}
        />
      ) : null}
    </Page>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-surface p-4">
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="mt-1 text-xl font-semibold">{value}</dd>
    </div>
  );
}

function statusLabel(status: DeveloperApp['status']): string {
  switch (status) {
    case 'published':
      return 'в каталоге';
    case 'in_review':
      return 'на проверке';
    case 'delisted':
      return 'снято';
    default:
      return 'черновик';
  }
}

function statusTone(status: DeveloperApp['status']): 'success' | 'warning' | 'neutral' | 'danger' {
  switch (status) {
    case 'published':
      return 'success';
    case 'in_review':
      return 'warning';
    case 'delisted':
      return 'danger';
    default:
      return 'neutral';
  }
}
